import { useQuery } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type { EmployeeWeekEntriesResponse } from './contractsMirror.js';

/**
 * One employee's individual shifts for one week (`GET /api/v1/timesheets?employeeId=&start=`).
 *
 * The week grid is an AGGREGATE: a cell says "Tuesday: 8.00" without saying whether that is one
 * shift or three. Correcting time needs the shifts themselves, so this fetches them — on demand,
 * only when a row is actually opened for editing, because a crew of fifteen would otherwise cost
 * fifteen requests to render a screen nobody is editing.
 *
 * Mounted only while a row is open for editing, so an unopened crew costs no requests at all.
 *
 * The key nests under `['timesheets']` so every timesheet mutation's existing
 * `invalidateQueries({ queryKey: ['timesheets'] })` refreshes these rows too — the grid totals and
 * the shifts behind them can never show two different answers.
 */
export function weekEntriesQueryKey(employeeId: number, weekStart: string): readonly unknown[] {
  return ['timesheets', 'entries', employeeId, weekStart] as const;
}

export function useWeekEntriesQuery(employeeId: number, weekStart: string) {
  return useQuery<EmployeeWeekEntriesResponse>({
    queryKey: weekEntriesQueryKey(employeeId, weekStart),
    queryFn: () =>
      apiFetchJson<EmployeeWeekEntriesResponse>(
        `/api/v1/timesheets?employeeId=${employeeId}&start=${encodeURIComponent(weekStart)}`,
      ),
  });
}
